import {
  Filter,
  GlProgram,
  GpuProgram,
  Matrix,
  UniformGroup,
  type FilterSystem,
  type RenderSurface,
  type Sprite,
  type Texture,
} from 'pixi.js';
import type { Rect } from '../logic/realm/ambient';

/**
 * Trees and plants swaying in the wind, weighted by a mask: where the mask
 * is white the painting shifts a few master pixels along the wind, by the
 * breeze of logic/realm/ambient's `swayLevel` (worked pixel by pixel, the
 * same sum); where it's black nothing is drawn. It goes on a copy of the
 * map tiles drawn over the real ones, as the sea's ripple does, so the rest
 * of the painting stays exactly as it was.
 *
 * A pixel only takes the shifted picture where the mask is white at the
 * spot it's taken from too, so nothing outside the mask (a building's wall,
 * the road's edge) is ever dragged into the moving leaves.
 *
 * The mask sprite is only read for where it sits; it must be on the stage
 * (it's never drawn).
 */
export class SwayFilter extends Filter {
  private readonly mask: Sprite;

  constructor(mask: Sprite) {
    const uniforms = new UniformGroup({
      uMaskMatrix: { value: new Matrix(), type: 'mat3x3<f32>' },
      uRect: { value: new Float32Array([0, 0, 1, 1]), type: 'vec4<f32>' },
      uWind: { value: new Float32Array([1, 0]), type: 'vec2<f32>' },
      uSway: { value: new Float32Array([0, 1, 1, 0]), type: 'vec4<f32>' },
      uPx: { value: 1, type: 'f32' },
    });
    super({
      glProgram: GlProgram.from({ vertex: VERTEX, fragment: FRAGMENT, name: 'sway-filter' }),
      gpuProgram: GpuProgram.from({
        vertex: { source: WGSL, entryPoint: 'mainVertex' },
        fragment: { source: WGSL, entryPoint: 'mainFragment' },
      }),
      resources: {
        swayUniforms: uniforms,
        uMaskTexture: mask.texture.source,
        uMaskSampler: mask.texture.source.style,
      },
    });
    this.mask = mask;
    mask.renderable = false;
  }

  /** Where the mask sits on the master. */
  set rect(r: Rect) {
    const u = this.resources.swayUniforms.uniforms.uRect as Float32Array;
    u[0] = r.x;
    u[1] = r.y;
    u[2] = r.w;
    u[3] = r.h;
  }

  /** The wind's direction, a unit vector. */
  setWind(x: number, y: number): void {
    const u = this.resources.swayUniforms.uniforms.uWind as Float32Array;
    u[0] = x;
    u[1] = y;
  }

  /** The clock (wrapped), seconds per sway, master pixels between gusts and
   *  the greatest shift in master pixels. */
  setSway(time: number, period: number, wavelength: number, amount: number): void {
    const u = this.resources.swayUniforms.uniforms.uSway as Float32Array;
    u[0] = time;
    u[1] = period;
    u[2] = wavelength;
    u[3] = amount;
  }

  /** Screen (CSS) pixels per master pixel: the zoom. */
  set pixelsPerMaster(px: number) {
    this.resources.swayUniforms.uniforms.uPx = px;
  }

  override apply(
    filterManager: FilterSystem,
    input: Texture,
    output: RenderSurface,
    clearMode: boolean,
  ): void {
    const u = this.resources.swayUniforms.uniforms;
    filterManager.calculateSpriteMatrix(u.uMaskMatrix as Matrix, this.mask);
    filterManager.applyFilter(this, input, output, clearMode);
  }
}

const VERTEX = /* glsl */ `in vec2 aPosition;
out vec2 vTextureCoord;
out vec2 vMaskUv;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;
uniform mat3 uMaskMatrix;

vec4 filterVertexPosition(void)
{
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
    return vec4(position, 0.0, 1.0);
}

vec2 filterTextureCoord(void)
{
    return aPosition * (uOutputFrame.zw * uInputSize.zw);
}

void main(void)
{
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
    vMaskUv = (uMaskMatrix * vec3(vTextureCoord, 1.0)).xy;
}
`;

const FRAGMENT = /* glsl */ `in vec2 vTextureCoord;
in highp vec2 vMaskUv;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform sampler2D uMaskTexture;
uniform vec4 uInputClamp;
uniform highp vec4 uInputSize;
uniform highp vec4 uRect;
uniform highp vec2 uWind;
uniform highp vec4 uSway;
uniform highp float uPx;

const highp float TAU = 6.2831853;

// logic/realm/ambient swayLevel, the same sum.
highp float level(highp vec2 p)
{
    highp float lambda = max(1.0, uSway.z);
    highp float per = max(0.001, uSway.y);
    highp float t = uSway.x;
    highp float phase = dot(p, uWind) / lambda + 0.37 * sin(TAU * (p.x - 0.6 * p.y) / (2.3 * lambda));
    highp float gust = 0.6 + 0.4 * sin(TAU * (t / (4.0 * per) - 0.31 * phase));
    return gust * (0.8 * sin(TAU * (t / per - phase))
        + 0.2 * sin(TAU * (2.35 * t / per - 2.7 * phase + 0.25)));
}

void main()
{
    vec2 inside = step(vec2(0.0), vMaskUv) * step(vMaskUv, vec2(1.0));
    float m = texture(uMaskTexture, vMaskUv).r * inside.x * inside.y;
    highp vec2 p = uRect.xy + vMaskUv * uRect.zw;
    highp vec2 shift = uWind * level(p) * uSway.w * m;
    // Only what is itself in the mask moves in.
    float inMask = texture(uMaskTexture, vMaskUv - shift / uRect.zw).r;
    vec4 here = texture(uTexture, vTextureCoord);
    vec4 moved = texture(uTexture, clamp(vTextureCoord - shift * uPx * uInputSize.zw, uInputClamp.xy, uInputClamp.zw));
    finalColor = mix(here, moved, inMask) * m;
}
`;

const WGSL = /* wgsl */ `
struct GlobalFilterUniforms {
  uInputSize:vec4<f32>,
  uInputPixel:vec4<f32>,
  uInputClamp:vec4<f32>,
  uOutputFrame:vec4<f32>,
  uGlobalFrame:vec4<f32>,
  uOutputTexture:vec4<f32>,
};

struct SwayUniforms {
  uMaskMatrix:mat3x3<f32>,
  uRect:vec4<f32>,
  uWind:vec2<f32>,
  uSway:vec4<f32>,
  uPx:f32,
};

@group(0) @binding(0) var<uniform> gfu: GlobalFilterUniforms;
@group(0) @binding(1) var uTexture: texture_2d<f32>;
@group(0) @binding(2) var uSampler : sampler;

@group(1) @binding(0) var<uniform> swayUniforms : SwayUniforms;
@group(1) @binding(1) var uMaskTexture: texture_2d<f32>;
@group(1) @binding(2) var uMaskSampler : sampler;

struct VSOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) uv : vec2<f32>,
  @location(1) maskUv : vec2<f32>,
};

const TAU: f32 = 6.2831853;

fn filterVertexPosition(aPosition:vec2<f32>) -> vec4<f32>
{
  var position = aPosition * gfu.uOutputFrame.zw + gfu.uOutputFrame.xy;
  position.x = position.x * (2.0 / gfu.uOutputTexture.x) - 1.0;
  position.y = position.y * (2.0*gfu.uOutputTexture.z / gfu.uOutputTexture.y) - gfu.uOutputTexture.z;
  return vec4(position, 0.0, 1.0);
}

fn filterTextureCoord(aPosition:vec2<f32>) -> vec2<f32>
{
  return aPosition * (gfu.uOutputFrame.zw * gfu.uInputSize.zw);
}

fn level(p: vec2<f32>) -> f32 {
  let s = swayUniforms.uSway;
  let lambda = max(1.0, s.z);
  let per = max(0.001, s.y);
  let t = s.x;
  let phase = dot(p, swayUniforms.uWind) / lambda + 0.37 * sin(TAU * (p.x - 0.6 * p.y) / (2.3 * lambda));
  let gust = 0.6 + 0.4 * sin(TAU * (t / (4.0 * per) - 0.31 * phase));
  return gust * (0.8 * sin(TAU * (t / per - phase)) + 0.2 * sin(TAU * (2.35 * t / per - 2.7 * phase + 0.25)));
}

@vertex
fn mainVertex(@location(0) aPosition : vec2<f32>) -> VSOutput {
  let uv = filterTextureCoord(aPosition);
  return VSOutput(
    filterVertexPosition(aPosition),
    uv,
    (swayUniforms.uMaskMatrix * vec3(uv, 1.0)).xy
  );
}

@fragment
fn mainFragment(
  @location(0) uv: vec2<f32>,
  @location(1) maskUv: vec2<f32>,
) -> @location(0) vec4<f32> {
  let inside = step(vec2(0.0), maskUv) * step(maskUv, vec2(1.0));
  let m = textureSample(uMaskTexture, uMaskSampler, maskUv).r * inside.x * inside.y;
  let p = swayUniforms.uRect.xy + maskUv * swayUniforms.uRect.zw;
  let shift = swayUniforms.uWind * level(p) * swayUniforms.uSway.w * m;
  let inMask = textureSample(uMaskTexture, uMaskSampler, maskUv - shift / swayUniforms.uRect.zw).r;
  let here = textureSample(uTexture, uSampler, uv);
  let moved = textureSample(uTexture, uSampler, clamp(uv - shift * swayUniforms.uPx * gfu.uInputSize.zw, gfu.uInputClamp.xy, gfu.uInputClamp.zw));
  return mix(here, moved, inMask) * m;
}
`;
